import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Flag, RefreshCw } from "lucide-react";
import { fmtDateTime, fmtMoney } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const ENTITY_ROUTE = {
  sale: (id) => `/sales?id=${id}`,
  proforma: (id) => `/proformas?id=${id}`,
  invoice: (id) => `/invoices?id=${id}`,
  receipt: (id) => `/receipts?id=${id}`,
};

const ENTITY_LABEL = {
  sale: "Sale",
  proforma: "Pro-forma",
  invoice: "Invoice",
  receipt: "Receipt",
};

export default function FlaggedItems() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/flagged-items");
      setRows(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const canView = user && ["operations", "accounts", "owner", "superadmin"].includes(user.role);
  if (!canView) {
    return (
      <PageBody>
        <p role="alert" className="border border-rose-200 bg-rose-50 text-rose-800 rounded p-4">Access restricted.</p>
      </PageBody>
    );
  }

  return (
    <>
      <PageHeader title="Flagged items" subtitle="Records flagged for follow-up across sales, pro-formas, invoices and receipts">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="flagged-refresh"><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </PageHeader>
      <PageBody>
        {error && <p role="alert" className="border border-rose-200 bg-rose-50 text-rose-800 rounded p-3 text-sm">{error}</p>}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <Table data-testid="flagged-items-table">
            <TableHeader><TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Number</TableHead>
              <TableHead>Party</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Flagged by</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>At</TableHead>
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="py-10 text-center text-slate-500">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="py-14 text-center text-slate-500" data-testid="flagged-empty">
                  <Flag className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                  Nothing flagged. Flags added on any sale, pro-forma, invoice or receipt appear here.
                </TableCell></TableRow>
              )}
              {rows.map((r) => {
                const buyer = r.buyer?.name || r.buyer_snapshot?.company || r.buyer_snapshot?.name || "—";
                const amountMinor = r.total_minor ?? r.amount_minor ?? 0;
                const link = ENTITY_ROUTE[r.entity_type]?.(r.id) || "#";
                return (
                  <TableRow key={`${r.entity_type}-${r.id}`} data-testid={`flagged-row-${r.entity_type}-${r.id}`}>
                    <TableCell><Badge variant="outline">{ENTITY_LABEL[r.entity_type]}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{r.number || "—"}</TableCell>
                    <TableCell>{buyer}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmtMoney(amountMinor, r.currency || "MYR")}</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{r.status || "—"}</Badge></TableCell>
                    <TableCell className="text-xs">{r.flag?.actor_email || r.flag?.flagged_by || "—"}</TableCell>
                    <TableCell className="text-xs max-w-md truncate" title={r.flag?.note}>{r.flag?.note}</TableCell>
                    <TableCell className="text-xs font-mono">{fmtDateTime(r.flag?.flagged_at)}</TableCell>
                    <TableCell>
                      <Button asChild size="sm" variant="outline"><Link to={link} data-testid={`flagged-open-${r.entity_type}-${r.id}`}>Open</Link></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </PageBody>
    </>
  );
}
